import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxes, faExclamationTriangle, faClipboardList, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

const DashboardGudang = () => {
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [categoryData, setCategoryData] = useState({
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: []
    }]
  });
  const [movementData, setMovementData] = useState({
    labels: [],
    datasets: []
  });

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh setiap menit
    return () => clearInterval(interval);
  }, []);

  // Modifikasi fungsi fetchDashboardData untuk mengatasi endpoint low-stock yang tidak tersedia
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }

      // Fetch inventory data
      const inventoryResponse = await axios.get('https://rumahakrilik.id/api/inventory/', {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      
      // Extract data
      const inventoryItems = Array.isArray(inventoryResponse.data) ? 
                            inventoryResponse.data : 
                            (inventoryResponse.data.results || []);
      
      // Set inventory state
      setInventory(inventoryItems);
      
      // PERBAIKAN: Gunakan data inventory untuk menghitung low stock
      // alih-alih mencoba mengakses endpoint low-stock yang tidak tersedia
      const lowStockItemsCalculated = inventoryItems.filter(item => {
        // Jika item memiliki minimum_stock, bandingkan dengan current_stock
        if (item.minimum_stock && item.current_stock) {
          return parseInt(item.current_stock) <= parseInt(item.minimum_stock);
        }
        // Jika menggunakan min_stock (field alternatif)
        if (item.min_stock && item.current_stock) {
          return parseInt(item.current_stock) <= parseInt(item.min_stock);
        }
        // Jika tidak ada minimum yang ditetapkan, cek jika stock sangat rendah (<=3)
        if (item.current_stock !== undefined) {
          return parseInt(item.current_stock) <= 3;
        }
        return false;
      });
      
      // Set low stock state
      setLowStockItems(lowStockItemsCalculated);
      console.log(`Found ${lowStockItemsCalculated.length} items with low stock`);
      
      // Prepare charts data
      prepareCategoryChartData(inventoryItems);
      prepareMovementChartData();
      
      toast.success('Data dashboard berhasil dimuat');
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Tambahkan pengecekan response status dan error message yang lebih spesifik
      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          toast.error('Sesi login telah berakhir, silakan login kembali');
        } else if (status === 403) {
          toast.error('Anda tidak memiliki izin untuk mengakses data gudang');
        } else {
          toast.error(`Error ${status}: ${error.response.data?.message || 'Gagal memuat data'}`);
        }
      } else if (error.request) {
        toast.error('Tidak dapat terhubung ke server. Periksa koneksi internet Anda.');
      } else {
        toast.error('Gagal memuat data dashboard gudang');
      }
    } finally {
      setLoading(false);
    }
  };

  const prepareCategoryChartData = (items) => {
    // Group by category
    const categories = {};
    
    items.forEach(item => {
      const category = item.category || 'Tidak Terkategorikan';
      if (!categories[category]) {
        categories[category] = 0;
      }
      categories[category] += parseInt(item.current_stock);
    });
    
    // Colors for categories
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', 
      '#9966FF', '#FF9F40', '#8BC34A', '#E91E63',
      '#3F51B5', '#00BCD4', '#009688', '#FFC107'
    ];
    
    // Convert to chart format
    const labels = Object.keys(categories);
    const data = Object.values(categories);
    const backgroundColors = labels.map((_, i) => colors[i % colors.length]);
    
    setCategoryData({
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: backgroundColors
      }]
    });
  };

  // For demonstration, using static data for movement chart
  const prepareMovementChartData = () => {
    // Get dates for the last 7 days
    const dates = Array(7).fill().map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return date.toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric' });
    });
    
    // Sample data for incoming and outgoing
    setMovementData({
      labels: dates,
      datasets: [
        {
          label: 'Barang Masuk',
          data: [5, 7, 2, 20, 15, 8, 10],
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: 'Barang Keluar',
          data: [3, 4, 6, 8, 12, 5, 8],
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1
        }
      ]
    });
  };

  // Gunakan useMemo untuk perhitungan yang tidak perlu diulang setiap render
  const totalItems = React.useMemo(() => inventory.length, [inventory]);
  const totalStock = React.useMemo(() => 
    inventory.reduce((sum, item) => sum + parseInt(item.current_stock || 0), 0), 
  [inventory]);
  const totalValue = inventory.reduce((sum, item) => {
    const price = parseFloat(item.price || 0);
    const stock = parseInt(item.current_stock || 0);
    return sum + (price * stock);
  }, 0);

  return (
    <MainLayout>
      <Container fluid className="py-4">
        <h1 className="mb-4">Dashboard Gudang</h1>
        
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Memuat data dashboard gudang...</p>
          </div>
        ) : (
          <>
            <Row className="mb-4">
              <Col md={3}>
                <Card className="h-100 dashboard-card">
                  <Card.Body className="text-center">
                    <div className="icon-wrapper mb-3">
                      <FontAwesomeIcon icon={faBoxes} className="fa-2x text-primary" />
                    </div>
                    <h2>{totalItems}</h2>
                    <p className="text-muted mb-0">Jenis Item</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="h-100 dashboard-card">
                  <Card.Body className="text-center">
                    <div className="icon-wrapper mb-3">
                      <FontAwesomeIcon icon={faWarehouse} className="fa-2x text-success" />
                    </div>
                    <h2>{totalStock}</h2>
                    <p className="text-muted mb-0">Total Stock</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="h-100 dashboard-card">
                  <Card.Body className="text-center">
                    <div className="icon-wrapper mb-3">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="fa-2x text-warning" />
                    </div>
                    <h2>{lowStockItems.length}</h2>
                    <p className="text-muted mb-0">Stock Menipis</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="h-100 dashboard-card">
                  <Card.Body className="text-center">
                    <div className="icon-wrapper mb-3">
                      <FontAwesomeIcon icon={faClipboardList} className="fa-2x text-info" />
                    </div>
                    <h2>Rp {totalValue.toLocaleString('id-ID')}</h2>
                    <p className="text-muted mb-0">Nilai Inventaris</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Row className="mb-4">
              <Col md={6}>
                <Card className="mb-4 h-100">
                  <Card.Header>
                    <h5 className="mb-0">Distribusi Stock per Kategori</h5>
                  </Card.Header>
                  <Card.Body>
                    {categoryData.labels.length > 0 ? (
                      <div style={{ height: '300px' }}>
                        <Pie 
                          data={categoryData} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'right'
                              }
                            }
                          }} 
                        />
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <p className="text-muted">Tidak ada data kategori tersedia</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="mb-4 h-100">
                  <Card.Header>
                    <h5 className="mb-0">Pergerakan Barang (7 Hari Terakhir)</h5>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ height: '300px' }}>
                      <Bar 
                        data={movementData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'top'
                            }
                          }
                        }} 
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Button variant="primary" onClick={fetchDashboardData}>
              Refresh Data
            </Button>
          </>
        )}
      </Container>
    </MainLayout>
  );
};

export default DashboardGudang;